import { CONTAINER, INVENTORY } from "./constants";
import { count, isItemId, type Inventory, type ItemId } from "./items";

/**
 * Where things sit, Minecraft's way: a row of slots. Your pack is one (the
 * first INVENTORY.hotbar are the hotbar, keys 1-9, and what's in your hand is
 * the selected one; the rest are the main grid), a chest another, a cargo
 * crate a single big slot.
 *
 * For the pack, the counts (Inventory) stay the truth of how much of each
 * thing you have: everything that gives or takes items works on them, and the
 * slots are then brought back in line with them (fitSlots). Moving things
 * between slots (moveSlot, moveAcross) never changes how much there is.
 */
export interface Stack {
  item: ItemId;
  n: number;
}

/** `size` long (longer only when there's more than fits, see fitSlots); null is an empty slot. */
export type Slots = (Stack | null)[];

/** The shape of a row of slots: how many, how many of them are a hotbar, and how much a stack holds. */
export interface SlotSpec {
  size: number;
  hotbar: number;
  stack: number;
  /** Holds only one kind of thing at a time (a cargo crate). */
  oneKind?: boolean;
}

export const PACK_SLOTS: SlotSpec = { size: INVENTORY.slots, hotbar: INVENTORY.hotbar, stack: INVENTORY.stack };
export const CHEST_SLOTS: SlotSpec = { size: CONTAINER.chest, hotbar: 0, stack: INVENTORY.stack };
export const CRATE_SLOTS: SlotSpec = { size: 1, hotbar: 0, stack: CONTAINER.crateHolds, oneKind: true };

export const HOTBAR_SLOTS = INVENTORY.hotbar;

/** Is slot `i` on the hotbar? */
export const onHotbar = (i: number, spec: SlotSpec = PACK_SLOTS): boolean => i < spec.hotbar;

export const emptySlots = (size: number = INVENTORY.slots): Slots => Array.from({ length: size }, () => null);

/** How many of each thing the slots hold. */
export function slotCounts(slots: Slots): Inventory {
  const out: Inventory = {};
  for (const s of slots) if (s) out[s.item] = (out[s.item] ?? 0) + s.n;
  return out;
}

/** Hotbar slots in order, then the main grid's. */
const order = (len: number): number[] => Array.from({ length: len }, (_, i) => i);

/**
 * `slots` brought in line with the counts in `inv`, keeping where things are.
 *
 * Taking out: something in your hand (`hand`, from hotbar slot `selected`)
 * comes out of that slot first, then the rest of the hotbar, then the grid;
 * anything else comes out of the grid first (last slot first), so the hotbar
 * stays put while you craft and stoke. Putting in: onto stacks of the same
 * thing that aren't full (hotbar first), then into the first empty slot
 * (hotbar first), the way Minecraft picks things up.
 *
 * If it still doesn't fit, stacks split by hand are merged back up; and if
 * there's more than the slots hold (a grapple given back with no room, a
 * pirate's plunder), the extra goes in slots past the end, for the panel to
 * show as overflow. Returns a new array; `slots` is left as it was.
 */
export function fitSlots(slots: readonly (Stack | null | undefined)[] | undefined, inv: Inventory, hand?: ItemId | null, selected = 0, spec: SlotSpec = PACK_SLOTS): Slots {
  const { size, stack } = spec;
  const hot = (i: number) => onHotbar(i, spec);
  // Start from a clean copy: the right length, nothing unknown, nothing empty or overfull.
  const out: Slots = emptySlots(size);
  for (const [i, s] of (slots ?? []).entries()) {
    if (!s || !isItemId(s.item) || !(s.n > 0)) continue;
    const fixed = { item: s.item, n: Math.min(Math.floor(s.n), stack) };
    if (fixed.n < 1) continue;
    if (i < out.length) out[i] = fixed;
    else out.push(fixed);
  }

  const have = slotCounts(out);
  const items = new Set<ItemId>([...(Object.keys(have) as ItemId[]), ...(Object.keys(inv) as ItemId[])]);
  // Take out first, so what's freed can take what comes in.
  for (const item of items) {
    let extra = count(have, item) - count(inv, item);
    if (extra <= 0) continue;
    const all = order(out.length);
    const takeFrom =
      item === hand && hot(selected)
        ? [selected, ...all.filter((i) => hot(i) && i !== selected), ...all.filter((i) => !hot(i))]
        : [...all.filter((i) => !hot(i)).reverse(), ...all.filter((i) => hot(i)).reverse()];
    for (const i of takeFrom) {
      const s = out[i];
      if (!s || s.item !== item) continue;
      const k = Math.min(extra, s.n);
      s.n -= k;
      extra -= k;
      if (s.n === 0) out[i] = null;
      if (extra === 0) break;
    }
  }
  for (const item of items) {
    let more = count(inv, item) - count(slotCounts(out), item);
    if (more <= 0) continue;
    more = topUp(out, item, more, spec);
    more = fillEmpty(out, item, more, spec);
    if (more > 0) {
      merge(out, item, stack);
      more = fillEmpty(out, item, topUp(out, item, more, spec), spec);
    }
    if (more > 0) {
      // Merge everything else too: hand-split stacks give their room back.
      for (const other of items) merge(out, other, stack);
      more = fillEmpty(out, item, topUp(out, item, more, spec), spec);
    }
    while (more > 0) {
      const k = Math.min(more, stack);
      out.push({ item, n: k });
      more -= k;
    }
  }
  // Overflow comes back in as soon as there's room for it.
  for (let i = size; i < out.length; i++) {
    const s = out[i];
    if (!s) continue;
    const left = fillEmpty(out, s.item, topUp(out.slice(0, size), s.item, s.n, spec), spec);
    out[i] = left > 0 ? { item: s.item, n: left } : null;
  }
  // Overflow slots that have emptied go (the slots themselves never shrink).
  while (out.length > size && out.some((s, i) => i >= size && !s)) {
    const gap = out.findIndex((s, i) => i >= size && !s);
    out.splice(gap, 1);
  }
  return out;
}

/** Add to stacks of `item` that aren't full, in slot order. Returns what's left over. */
function topUp(slots: Slots, item: ItemId, n: number, spec: SlotSpec): number {
  for (const s of slots) {
    if (n <= 0) break;
    if (!s || s.item !== item || s.n >= spec.stack) continue;
    const k = Math.min(n, spec.stack - s.n);
    s.n += k;
    n -= k;
  }
  return n;
}

/** Put `item` in empty slots (not overflow), in slot order. Returns what's left over. */
function fillEmpty(slots: Slots, item: ItemId, n: number, spec: SlotSpec): number {
  for (let i = 0; i < spec.size && n > 0; i++) {
    if (slots[i]) continue;
    const k = Math.min(n, spec.stack);
    slots[i] = { item, n: k };
    n -= k;
  }
  return n;
}

/** Gather `item` into as few stacks as it'll go in, kept in the earliest of the slots it's in. */
function merge(slots: Slots, item: ItemId, stack: number): void {
  const at = order(slots.length).filter((i) => slots[i]?.item === item);
  let total = at.reduce((sum, i) => sum + slots[i]!.n, 0);
  for (const i of at) {
    const k = Math.min(total, stack);
    slots[i] = k > 0 ? { item, n: k } : null;
    total -= k;
  }
}

/**
 * Move `n` from slot `from` to slot `to` of the same slots, in place. Onto an
 * empty slot it goes across (splitting the stack if it's not all of it); onto
 * the same thing it tops that stack up as far as it'll go; onto something else
 * the two swap, but only when it's the whole stack. Returns whether anything moved.
 */
export function moveSlot(slots: Slots, from: number, to: number, n: number, spec: SlotSpec = PACK_SLOTS): boolean {
  if (from === to) return false;
  return moveAcross(slots, spec, from, slots, spec, to, n) > 0;
}

/**
 * Move `n` from slot `from` of `src` to slot `to` of `dst` (which may be the
 * same slots), in place, by the same rules as moveSlot: onto an empty slot,
 * onto the same thing as far as it'll go, or a whole stack swapped with
 * something else. Nothing goes into overflow, a stack never ends up bigger
 * than its slots allow, and a crate never ends up with two kinds of thing.
 * Returns how many went across (a swap counts as all of it); 0 if nothing moved.
 */
export function moveAcross(src: Slots, srcSpec: SlotSpec, from: number, dst: Slots, dstSpec: SlotSpec, to: number, n: number): number {
  if (![from, to, n].every(Number.isInteger)) return 0;
  if (from < 0 || to < 0 || from >= src.length || to >= dstSpec.size || (src === dst && from === to)) return 0;
  const a = src[from];
  if (!a || n < 1 || n > a.n) return 0;
  const b = dst[to];
  if (!b || b.item === a.item) {
    // (Within the same slots nothing new comes in, so a crate's overflow can still be tidied into it.)
    if (src !== dst && !holdsOnly(dst, dstSpec, a.item, -1)) return 0;
    const k = Math.min(n, dstSpec.stack - (b?.n ?? 0));
    if (k < 1) return 0;
    if (b) b.n += k;
    else dst[to] = { item: a.item, n: k };
    a.n -= k;
    if (a.n === 0) src[from] = null;
    return k;
  }
  // A swap: the whole stack, each fitting where it goes.
  if (n !== a.n || a.n > dstSpec.stack || b.n > srcSpec.stack) return 0;
  if (src !== dst && (!holdsOnly(dst, dstSpec, a.item, to) || !holdsOnly(src, srcSpec, b.item, from))) return 0;
  src[from] = b;
  dst[to] = a;
  return a.n;
}

/** Can `item` go in these slots, as far as their kind goes: anything can, except into a crate holding something else (slot `except` aside). */
function holdsOnly(slots: Slots, spec: SlotSpec, item: ItemId, except: number): boolean {
  return !spec.oneKind || slots.every((s, i) => i === except || !s || s.item === item);
}

/**
 * Shift-click across: as much of the stack in slot `from` of `src` as will go
 * into `dst`, onto stacks of the same thing first and then into empty slots
 * (the main grid's before the hotbar's). In place; returns how many went.
 */
export function quickMoveAcross(src: Slots, srcSpec: SlotSpec, from: number, dst: Slots, dstSpec: SlotSpec): number {
  const a = src[from];
  if (!a || !Number.isInteger(from) || from < 0) return 0;
  const item = a.item;
  const all = order(dstSpec.size);
  const same = all.filter((i) => dst[i]?.item === item);
  const empty = [...all.filter((i) => !onHotbar(i, dstSpec)), ...all.filter((i) => onHotbar(i, dstSpec))].filter((i) => !dst[i]);
  let moved = 0;
  for (const to of [...same, ...empty]) {
    const s = src[from];
    if (!s) break;
    // Into an empty slot only as much as a stack of where it's from holds, so one click doesn't pour a crate into one pack slot.
    moved += moveAcross(src, srcSpec, from, dst, dstSpec, to, Math.min(s.n, dstSpec.stack));
  }
  return moved;
}

/**
 * Where shift-click sends the stack in pack slot `from`: across between the
 * hotbar and the main grid, onto a stack of the same thing with room, or else
 * the first empty slot there. Null if there's nowhere.
 */
export function quickMoveTarget(slots: Slots, from: number): number | null {
  const s = slots[from];
  if (!s) return null;
  const there = order(INVENTORY.slots).filter((i) => onHotbar(i) !== onHotbar(from) || from >= INVENTORY.slots);
  const same = there.find((i) => slots[i]?.item === s.item && slots[i]!.n < INVENTORY.stack);
  if (same !== undefined) return same;
  return there.find((i) => !slots[i]) ?? null;
}

/** The first empty slot in the main grid (or, failing that, anywhere), for putting something away. */
export function freeSlot(slots: Slots): number | null {
  const all = order(INVENTORY.slots);
  return all.find((i) => !onHotbar(i) && !slots[i]) ?? all.find((i) => !slots[i]) ?? null;
}

/** How much more of `item` the slots take without overflowing (none, in a crate holding something else). */
export function roomIn(slots: Slots, spec: SlotSpec, item: ItemId): number {
  if (!holdsOnly(slots, spec, item, -1)) return 0;
  let room = 0;
  for (let i = 0; i < spec.size; i++) {
    const s = slots[i];
    if (!s) room += spec.stack;
    else if (s.item === item) room += Math.max(0, spec.stack - s.n);
  }
  return room;
}
